import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { ColumnTable } from 'arquero'
import { PinnedMorphotype } from 'redux/slices'
import { DifferentialFeaturesComparison } from 'redux/slices/types'
import { DifferentialFeaturesTaskInput, startDifferentialFeaturesTask, TaskStatus } from 'utils/api'
import { convertMorphotypeIdsToCellIdsFromDataFrame } from './Pinned/DifferentialFeatures/morphotypeListUtils'

interface UseRecomputeDifferentialFeaturesReturn {
  recomputeForMigratedMorphotypes: (
    comparisons: DifferentialFeaturesComparison[] | undefined,
    migratedMorphotypeIds: number[],
    sessionId: number,
    versionId: number,
    dataFrame: ColumnTable,
    pinnedCells: PinnedMorphotype[]
  ) => Promise<DifferentialFeaturesComparison[]>
  recomputeAllComparisons: (
    comparisons: DifferentialFeaturesComparison[] | undefined,
    sessionId: number,
    versionId: number,
    dataFrame: ColumnTable,
    pinnedCells: PinnedMorphotype[]
  ) => Promise<DifferentialFeaturesComparison[]>
}

/**
 * Custom hook that provides functions to recompute differential features comparisons
 * when morphotypes are migrated or when cell data changes (e.g., prefiltering).
 */
export default function useRecomputeDifferentialFeatures(): UseRecomputeDifferentialFeaturesReturn {
  const queryClient = useQueryClient()

  /**
   * Recomputes differential features comparisons that reference migrated morphotypes.
   * Uses DataFrame for memory efficiency.
   * Returns updated comparisons array with new pending tasks for migrated comparisons.
   */
  const recomputeForMigratedMorphotypes = useCallback(
    async (
      comparisons: DifferentialFeaturesComparison[] | undefined,
      migratedMorphotypeIds: number[],
      sessionId: number,
      versionId: number,
      dataFrame: ColumnTable,
      pinnedCells: PinnedMorphotype[]
    ): Promise<DifferentialFeaturesComparison[]> => {
      if (!comparisons || comparisons.length === 0 || migratedMorphotypeIds.length === 0) {
        return comparisons || []
      }

      // Process all comparisons in parallel
      const comparisonPromises = comparisons.map(async (comparison) => {
        const includesMigratedMorphotype =
          comparison.morphotypeIds1.some((id) => migratedMorphotypeIds.includes(id)) ||
          comparison.morphotypeIds2.some((id) => migratedMorphotypeIds.includes(id))

        if (includesMigratedMorphotype) {
          // Recompute this comparison with updated cell IDs using DataFrame
          const payload: DifferentialFeaturesTaskInput = {
            sessionId,
            versionId,
            cellIdList1: convertMorphotypeIdsToCellIdsFromDataFrame(
              comparison.morphotypeIds1,
              comparison.morphotypeIds2,
              dataFrame,
              pinnedCells
            ),
            cellIdList2: convertMorphotypeIdsToCellIdsFromDataFrame(
              comparison.morphotypeIds2,
              comparison.morphotypeIds1,
              dataFrame,
              pinnedCells
            ),
          }

          try {
            const result = await queryClient.fetchQuery(
              ['startDifferentialFeaturesTask', payload],
              startDifferentialFeaturesTask
            )
            const { task_id: taskId } = result.data

            return {
              morphotypeIds1: comparison.morphotypeIds1,
              morphotypeIds2: comparison.morphotypeIds2,
              taskId,
              status: TaskStatus.PENDING,
              topFeatures: undefined,
            }
          } catch (error) {
            console.error('Error recomputing differential features for migrated morphotypes', error)
            return {
              ...comparison,
              status: TaskStatus.FAILED,
              topFeatures: undefined,
            }
          }
        }
        return comparison
      })

      return Promise.all(comparisonPromises)
    },
    [queryClient]
  )

  /**
   * Recomputes ALL differential features comparisons with updated cell IDs.
   * Used when prefiltering or other operations that affect all morphotype cell counts.
   * Uses DataFrame for memory efficiency.
   * Returns updated comparisons array with new pending tasks.
   */
  const recomputeAllComparisons = useCallback(
    async (
      comparisons: DifferentialFeaturesComparison[] | undefined,
      sessionId: number,
      versionId: number,
      dataFrame: ColumnTable,
      pinnedCells: PinnedMorphotype[]
    ): Promise<DifferentialFeaturesComparison[]> => {
      if (!comparisons || comparisons.length === 0) {
        return comparisons || []
      }

      // Process all comparisons in parallel
      const comparisonPromises = comparisons.map(async (comparison) => {
        const payload: DifferentialFeaturesTaskInput = {
          sessionId,
          versionId,
          cellIdList1: convertMorphotypeIdsToCellIdsFromDataFrame(
            comparison.morphotypeIds1,
            comparison.morphotypeIds2,
            dataFrame,
            pinnedCells
          ),
          cellIdList2: convertMorphotypeIdsToCellIdsFromDataFrame(
            comparison.morphotypeIds2,
            comparison.morphotypeIds1,
            dataFrame,
            pinnedCells
          ),
        }

        try {
          const result = await queryClient.fetchQuery(
            ['startDifferentialFeaturesTask', payload],
            startDifferentialFeaturesTask
          )
          const { task_id: taskId } = result.data

          // Return new comparison with new taskId and reset topFeatures
          return {
            morphotypeIds1: comparison.morphotypeIds1,
            morphotypeIds2: comparison.morphotypeIds2,
            taskId,
            status: TaskStatus.PENDING,
            topFeatures: undefined, // Reset old results
          }
        } catch (error) {
          console.error('Error recomputing differential features', error)
          // If recomputation fails, mark as failed and clear old results to avoid showing stale data
          return {
            ...comparison,
            status: TaskStatus.FAILED,
            topFeatures: undefined, // Clear stale results
          }
        }
      })

      return Promise.all(comparisonPromises)
    },
    [queryClient]
  )

  return {
    recomputeForMigratedMorphotypes,
    recomputeAllComparisons,
  }
}
